/**
 * Cart BFCache Fix - Handles browser back button navigation from checkout
 *
 * CRITICAL FIX: Addresses cart quantity reset bug when users navigate back from
 * Shopify checkout using the browser back button (iOS Safari back gesture).
 *
 * Root Cause:
 * - When navigating to checkout, the page is stored in browser's bfcache (back/forward cache)
 * - On back navigation, the page is restored from cache with stale cart state
 * - The cart-store.js initializes from the cached #cart-data JSON, not fresh Shopify cart
 * - This causes quantities to revert to old values (often 1 instead of calculated values)
 *
 * Solution (Enhanced):
 * - Listen for 'pageshow' event with persisted=true (indicates bfcache restoration)
 * - FIRST: Fetch fresh cart data from /cart.js and update #cart-data DOM element
 * - THEN: Force cart refresh from Shopify API to update cart-store state
 * - This two-step approach ensures both the DOM and state management are synchronized
 * - Also handles 'popstate' events for additional navigation safeguards
 * - Update UI to reflect accurate cart quantities via Cart subscription system
 *
 * @see https://web.dev/bfcache/
 * @see /Users/ali/my-projects/stoneandtileprojects/docs/issues/cart-quantity-reset-after-checkout-back-navigation.md
 * @version 1.2.0
 * @date 2025-10-19
 * @changelog
 *   - v1.2.0: Added mergeDuplicateCartEntries() to fix duplicate line item bug
 *   - v1.1.0: Enhanced to update #cart-data element before calling Cart.refresh()
 *   - v1.0.0: Initial implementation with basic bfcache and popstate handling
 */

import Cart from 'cart-store';
import CartDebug from 'cart-debug-logger';

/**
 * Merge duplicate cart entries (same variant_id)
 * This fixes the bug where multiple entries exist for the same product variant
 * @param {Object} cartData - Cart data from Shopify API
 * @returns {Object} Cart data with duplicates merged, or original if no API needed
 */
async function mergeDuplicateCartEntries(cartData) {
  if (!cartData || !cartData.items || cartData.items.length === 0) {
    return cartData;
  }

  // Detect duplicates by variant_id
  const variantMap = new Map();
  const duplicates = [];

  cartData.items.forEach((item, index) => {
    const variantId = item.variant_id;
    if (variantMap.has(variantId)) {
      // Found a duplicate
      const firstIndex = variantMap.get(variantId);
      duplicates.push({
        variant_id: variantId,
        first_key: cartData.items[firstIndex].key,
        first_quantity: cartData.items[firstIndex].quantity,
        duplicate_key: item.key,
        duplicate_quantity: item.quantity,
        total_quantity: cartData.items[firstIndex].quantity + item.quantity
      });
    } else {
      variantMap.set(variantId, index);
    }
  });

  // If no duplicates, return original data unchanged
  if (duplicates.length === 0) {
    return cartData;
  }

  // Log detected duplicates
  if (window.Shopify?.designMode || window.cartBFCacheDebug) {
    console.warn('[Cart BFCache Fix] Detected duplicate cart entries:', duplicates);
  }

  // Merge duplicates via Shopify cart API
  // Strategy: Keep first entry with combined quantity, remove duplicates
  try {
    for (const dup of duplicates) {
      // Update first entry to have combined quantity
      await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: dup.first_key,
          quantity: dup.total_quantity
        })
      });

      // Remove duplicate entry by setting quantity to 0
      await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: dup.duplicate_key,
          quantity: 0
        })
      });

      if (window.Shopify?.designMode || window.cartBFCacheDebug) {
        console.log(`[Cart BFCache Fix] Merged variant ${dup.variant_id}: ${dup.first_quantity} + ${dup.duplicate_quantity} = ${dup.total_quantity}`);
      }
    }

    // Fetch updated cart data after merging
    const updatedCartData = await fetch('/cart.js').then(res => res.json());
    return updatedCartData;

  } catch (error) {
    console.error('[Cart BFCache Fix] Error merging duplicate entries:', error);
    CartDebug.log('MERGE_FAILED', {
      error: error.message,
      duplicates: duplicates
    });
    // Return original data if merge fails
    return cartData;
  }
}

/**
 * Handle page restoration from bfcache
 * @param {PageTransitionEvent} event - The pageshow event
 */
async function handleBFCacheRestoration(event) {
  // Check if page was restored from bfcache
  if (!event.persisted) {
    // Normal page load, not from cache - no action needed
    return;
  }

  // Log bfcache restoration event
  CartDebug.log('BFCACHE_RESTORE', {
    persisted: event.persisted,
    url: window.location.href
  });

  // Debug logging (only in development)
  if (window.Shopify?.designMode || window.cartBFCacheDebug) {
    console.log('[Cart BFCache Fix] Page restored from bfcache, refreshing cart...');
    console.log('[Cart BFCache Fix] Current cart state (from cache):', Cart.getState().cart);
  }

  const cachedCart = Cart.getState().cart;
  CartDebug.logCartState(cachedCart, 'BEFORE_BFCACHE_REFRESH');
  CartDebug.detectDuplicates(cachedCart);

  try {
    // CRITICAL FIX: Update the cached cart-data element FIRST before Cart.refresh()
    // This prevents cart-store from re-initializing with stale data
    const freshCartData = await fetch('/cart.js').then(res => res.json());
    const cartDataElement = document.getElementById('cart-data');

    CartDebug.logCartState(freshCartData, 'FRESH_FROM_SERVER');
    CartDebug.detectDuplicates(freshCartData);
    CartDebug.compareCartStates(cachedCart, freshCartData);

    // ADDITIONAL FIX: Merge duplicate cart entries before updating state
    // This addresses the secondary bug where duplicate line items exist
    const mergedCartData = await mergeDuplicateCartEntries(freshCartData);

    if (mergedCartData !== freshCartData) {
      if (window.Shopify?.designMode || window.cartBFCacheDebug) {
        console.log('[Cart BFCache Fix] Merged duplicate entries detected');
        CartDebug.log('DUPLICATES_MERGED', {
          before: freshCartData.item_count,
          after: mergedCartData.item_count
        });
      }
    }

    if (cartDataElement && mergedCartData) {
      // Update the DOM element that cart-store reads from with MERGED data
      cartDataElement.textContent = JSON.stringify(mergedCartData);

      if (window.Shopify?.designMode || window.cartBFCacheDebug) {
        console.log('[Cart BFCache Fix] Updated cart-data element with fresh merged data');
        console.log('[Cart BFCache Fix] Fresh cart from server:', mergedCartData);
      }
    }

    // Force refresh cart from Shopify server
    // This ensures we get the accurate cart state, not the cached one
    await Cart.refresh();

    const refreshedCart = Cart.getState().cart;
    CartDebug.logCartState(refreshedCart, 'AFTER_CART_REFRESH');

    if (window.Shopify?.designMode || window.cartBFCacheDebug) {
      console.log('[Cart BFCache Fix] Cart refreshed successfully');
      console.log('[Cart BFCache Fix] Updated cart state:', refreshedCart);
    }

    // If cart drawer/modal is open, it will automatically update via Cart subscription
    // If on cart page, the page will re-render with correct quantities

  } catch (error) {
    console.error('[Cart BFCache Fix] Error refreshing cart after bfcache restoration:', error);
    CartDebug.log('CART_FAILED', {
      error: error.message,
      stack: error.stack,
      context: 'BFCACHE_RESTORE'
    });

    // Fallback: reload the page to ensure clean state
    // This is a last resort but ensures data accuracy
    if (window.location.pathname.includes('/cart') ||
        document.querySelector('.modal--cart')?.getAttribute('aria-hidden') === 'false') {
      console.warn('[Cart BFCache Fix] Forcing page reload due to cart refresh error');
      window.location.reload();
    }
  }
}

/**
 * Handle popstate events (browser navigation)
 * Additional safeguard for history-based navigation
 * @param {PopStateEvent} event - The popstate event
 */
function handlePopState(event) {
  // Log popstate event
  CartDebug.log('POPSTATE', {
    url: window.location.href,
    pathname: window.location.pathname
  });

  // Small delay to allow page to settle after navigation
  setTimeout(async () => {
    // Check if we're on a cart-related page or have cart drawer open
    const isCartPage = window.location.pathname.includes('/cart');
    const isCartDrawerOpen = document.querySelector('.modal--cart')?.getAttribute('aria-hidden') === 'false';

    if (isCartPage || isCartDrawerOpen) {
      if (window.Shopify?.designMode || window.cartBFCacheDebug) {
        console.log('[Cart BFCache Fix] Popstate detected on cart context, refreshing cart...');
      }

      const beforeCart = Cart.getState().cart;
      CartDebug.logCartState(beforeCart, 'BEFORE_POPSTATE_REFRESH');

      try {
        // Update cart-data element first, then refresh
        const freshCartData = await fetch('/cart.js').then(res => res.json());
        const cartDataElement = document.getElementById('cart-data');

        CartDebug.logCartState(freshCartData, 'POPSTATE_FRESH_FROM_SERVER');
        CartDebug.compareCartStates(beforeCart, freshCartData);

        // Merge duplicate entries (same fix as bfcache handler)
        const mergedCartData = await mergeDuplicateCartEntries(freshCartData);

        if (mergedCartData !== freshCartData) {
          if (window.Shopify?.designMode || window.cartBFCacheDebug) {
            console.log('[Cart BFCache Fix] Popstate: Merged duplicate entries');
          }
        }

        if (cartDataElement && mergedCartData) {
          cartDataElement.textContent = JSON.stringify(mergedCartData);
        }

        await Cart.refresh();

        const afterCart = Cart.getState().cart;
        CartDebug.logCartState(afterCart, 'AFTER_POPSTATE_REFRESH');
      } catch (error) {
        console.error('[Cart BFCache Fix] Error refreshing cart after popstate:', error);
        CartDebug.log('CART_FAILED', {
          error: error.message,
          stack: error.stack,
          context: 'POPSTATE'
        });
      }
    }
  }, 100);
}

/**
 * Initialize the BFCache fix
 */
function init() {
  // Primary fix: Handle bfcache restoration
  window.addEventListener('pageshow', handleBFCacheRestoration, false);

  // Secondary safeguard: Handle history navigation
  window.addEventListener('popstate', handlePopState, false);

  if (window.Shopify?.designMode || window.cartBFCacheDebug) {
    console.log('[Cart BFCache Fix] Initialized - monitoring for bfcache restoration and browser navigation');
  }
}

/**
 * Cleanup function (if needed for SPA navigation)
 */
function destroy() {
  window.removeEventListener('pageshow', handleBFCacheRestoration, false);
  window.removeEventListener('popstate', handlePopState, false);
}

// Auto-initialize when module loads
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Export for manual control if needed
export { init, destroy, handleBFCacheRestoration, handlePopState };
export default { init, destroy };
